import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import VendorlyLogo from "@/components/VendorlyLogo";
import {
  ChartLine, Users, QrCode, ChatCircleDots, PaperPlaneTilt, Gear, SignOut, Storefront, CreditCard, Shield,
} from "@phosphor-icons/react";
import { Toaster } from "sonner";

const vendorNav = [
  { to: "/app", label: "Dashboard", icon: ChartLine, testid: "nav-dashboard", end: true },
  { to: "/app/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/app/qr", label: "QR Code", icon: QrCode, testid: "nav-qr" },
  { to: "/app/templates", label: "Templates", icon: ChatCircleDots, testid: "nav-templates" },
  { to: "/app/messages", label: "Messages", icon: PaperPlaneTilt, testid: "nav-messages" },
  { to: "/app/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
  { to: "/app/settings", label: "Settings", icon: Gear, testid: "nav-settings" },
];

function SubBanner({ user }) {
  if (user?.is_admin) return null;
  const sub = user?.subscription_status;
  const sent = user?.messages_sent_cycle ?? 0;
  const freeUsed = user?.free_sends_used ?? 0;
  const isFree = sub !== "active";
  const cap = user?.free_recipients_cap ?? 20;
  // Free tier: show banner until they subscribe. Paid tier: show only when >= 80% used.
  if (!isFree && sent < 400) return null;
  return (
    <div className="mb-6 rounded-2xl bg-brand-warning/15 border border-brand-warning/40 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="sub-banner">
      <div className="text-sm">
        <span className="font-heading font-bold">
          {isFree
            ? (freeUsed === 0
                ? `Free demo · 1 campaign to up to ${cap} customers`
                : "Free demo used")
            : `${sent}/500 messages used this cycle`}
        </span>
        <span className="text-brand-text/75">
          {isFree
            ? (freeUsed === 0
                ? " — see the full experience once, then subscribe for ₹99."
                : " — subscribe for ₹99 to unlock 500 messages/month.")
            : ` — only ${Math.max(0, 500 - sent)} remaining. Renews with your subscription.`}
        </span>
      </div>
      <Link to="/app/billing" className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full px-4 py-2 text-sm font-semibold" data-testid="banner-pay-btn">
        <CreditCard size={16} weight="duotone" /> {isFree ? (freeUsed === 0 ? "See plan" : "Pay ₹99") : "Manage"}
      </Link>
    </div>
  );
}

function VerifyBanner({ user }) {
  if (!user || user.is_admin) return null;
  if (user.email_verified) return null;
  return (
    <div className="mb-6 rounded-2xl bg-brand-secondary/10 border border-brand-secondary/30 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="verify-banner">
      <div className="text-sm">
        <span className="font-heading font-bold">Please verify your email</span>
        <span className="text-brand-text/75"> — we sent a link to <strong>{user.email}</strong>. Needed for password resets & billing receipts.</span>
      </div>
      <Link to="/app/settings" className="inline-flex items-center gap-2 border-2 border-brand-secondary hover:bg-brand-secondary hover:text-white text-brand-secondary rounded-full px-4 py-2 text-sm font-semibold" data-testid="verify-banner-cta">
        Resend / manage
      </Link>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const initial = user?.business_name?.[0]?.toUpperCase() || "V";
  const nav = [...vendorNav];
  if (user?.is_admin) nav.push({ to: "/admin", label: "Admin", icon: Shield, testid: "nav-admin", isAdmin: true });
  const isAdminRoute = loc.pathname.startsWith("/admin");

  return (
    <div className="min-h-screen bg-brand-bg text-brand-text">
      <div className="flex">
        <aside className="hidden lg:flex flex-col w-64 min-h-screen border-r border-brand-border bg-white/60 backdrop-blur-sm p-6 sticky top-0">
          <Link to="/" className="flex items-center gap-2 mb-10" data-testid="brand-link">
            <VendorlyLogo size={30} />
            <span className="font-heading font-bold text-xl tracking-tight">Vendorly</span>
          </Link>
          <nav className="flex flex-col gap-1">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-colors ${
                    n.isAdmin
                      ? isActive
                        ? "bg-brand-secondary text-white mt-3 border-t border-brand-border pt-3 rounded-t-none"
                        : "text-brand-secondary hover:bg-brand-secondary/10 mt-3 border-t border-brand-border pt-4"
                      : isActive
                        ? "bg-brand-primary text-white"
                        : "text-brand-text hover:bg-brand-surface hover:text-brand-primary"
                  }`}>
                <n.icon size={20} weight="duotone" />
                <span className="text-sm">{n.label}</span>
                {n.isAdmin && <span className="ml-auto text-[9px] uppercase tracking-widest bg-brand-secondary/20 text-brand-secondary px-1.5 py-0.5 rounded">Owner</span>}
              </NavLink>
            ))}
          </nav>
          <div className="mt-auto pt-6 border-t border-brand-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-brand-secondary text-white flex items-center justify-center font-heading font-bold">
                {initial}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                  {user?.business_name}
                  {user?.is_admin && <span className="text-[10px] uppercase tracking-widest bg-brand-secondary text-white px-1.5 py-0.5 rounded">Admin</span>}
                </div>
                <div className="text-xs text-brand-textMuted truncate">{user?.email}</div>
              </div>
            </div>
            <button onClick={async () => { await logout(); navigate("/"); }} data-testid="logout-btn"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-brand-border text-brand-text hover:bg-brand-surface hover:border-brand-borderHover transition-colors">
              <SignOut size={18} weight="duotone" /> <span className="text-sm">Sign out</span>
            </button>
          </div>
        </aside>

        <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-white/80 backdrop-blur-md border-b border-brand-border flex items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
            <VendorlyLogo size={26} />
            <span className="font-heading font-bold">Vendorly</span>
          </Link>
          <div className="flex items-center gap-2">
            {user?.is_admin && (
              <Link to="/admin" data-testid="mobile-admin-link"
                className="inline-flex items-center gap-1.5 bg-brand-secondary text-white rounded-full px-3 py-1.5 text-xs font-semibold">
                <Shield size={14} weight="duotone" /> Admin
              </Link>
            )}
            <button onClick={async () => { await logout(); navigate("/"); }} data-testid="logout-btn-mobile" className="text-sm text-brand-text">
              <SignOut size={20} weight="duotone" />
            </button>
          </div>
        </div>

        <main className="flex-1 min-w-0 pt-16 lg:pt-0">
          <div className="p-6 md:p-8 lg:p-12">
            {!isAdminRoute && <VerifyBanner user={user} />}
            {!isAdminRoute && <SubBanner user={user} />}
            {children}
          </div>
          <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-brand-border flex justify-around py-2 z-40">
            {vendorNav.slice(0, 5).map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`m-${n.testid}`}
                className={({ isActive }) => `flex flex-col items-center gap-1 px-3 py-1 ${isActive ? "text-brand-primary" : "text-brand-textMuted"}`}>
                <n.icon size={20} weight="duotone" />
                <span className="text-[10px] font-medium">{n.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="lg:hidden h-16" />
        </main>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
